/**
 * The canonical ingredient vocabulary — what an ingredient IS, in one place.
 *
 * Single source of truth for every layer that has an opinion about ingredient
 * identity:
 *   - pantry matching   (src/services/ingredientCoverageService.ts — the recipe
 *                        screen — and src/services/pantryMatch.ts, which every
 *                        ranking, planner card and pantry deduction reads)
 *   - grocery list      (src/services/groceryListService.ts)
 *   - substitutions     (src/services/ingredientSubstitutionService.ts)
 *   - step chips        (src/utils/resolveStepIngredients.ts)
 *
 * Lives in src/utils (not api/_lib) on purpose, exactly like
 * `dishTypeVocabulary.ts`: it has no dependencies, so client code, API handlers
 * and tooling can all import it. The Gemini fallback layer stays behind in
 * api/_lib, which needs firebase-admin.
 *
 * Why this exists: five services each kept their own private notes about what an
 * ingredient means, and they disagreed. With a pantry holding 5 limes and a tub
 * of butter, the recipe screen said you HAD "2 tbsp lime juice" while the
 * grocery list told you to BUY it; both screens said you had "4 kaffir lime
 * leaves" and "2 tbsp peanut butter", which you did not. Those answers came from
 * substring overlap, not from knowing what a lime is.
 *
 * THE `form` / `base` PAIR IS THE POINT. `normalizeIngredientName` strips
 * "juice", "zest", "powder" and "paste" as noise, so `lime juice` and `lime`
 * both flatten to `lime` and the distinction is gone before anything can use it.
 * Here `lime-juice` is its own entry with `base: 'lime'`, which is what lets a
 * caller say "you have limes, that probably covers lime juice" while still
 * saying "limes are not lime leaves".
 *
 * Adding an entry: prefer a new `base` entry plus derived forms over a
 * free-floating id. Every id must be reachable from at least one surface form in
 * each language it is used in, or the resolver can never return it.
 */

export type IngredientForm =
  | 'whole'
  | 'juice'
  | 'zest'
  | 'powder'
  | 'paste'
  | 'puree'
  | 'flakes'
  | 'dried';

export type IngredientCategory =
  | 'dairy'
  | 'protein'
  | 'grain'
  | 'fat'
  | 'vegetable'
  | 'fruit'
  | 'aromatic'
  | 'herb'
  | 'spice'
  | 'acid'
  | 'condiment'
  | 'liquid'
  | 'thickener'
  | 'sweetener'
  | 'nut'
  | 'seed'
  | 'chocolate'
  | 'leavening'
  | 'other';

export interface IngredientEntry {
  /** Stable kebab-case identity. Never rename one — migrate instead. */
  id: string;
  /** English surface forms. First entry is the display name. */
  en: string[];
  /** French surface forms. First entry is the display name. */
  fr: string[];
  category: IngredientCategory;
  /** Present when this entry is a form of something else. */
  form?: IngredientForm;
  /** The whole ingredient this one comes from. `lime-juice` -> `lime`. */
  base?: string;
  /**
   * A category word rather than a food: `cheese`, `fish`, `wine`, `broth`.
   *
   * These lose to a specific match even when their surface form is longer.
   * Without it "fromage feta coupé en dés" resolves to `cheese`, because
   * "fromage" (7 characters) beats "feta" (4) under longest-wins — technically
   * true and useless on a shopping list.
   */
  generic?: boolean;
  /**
   * The category this is a kind of: `feta` -> `cheese`, `sherry` -> `wine`.
   *
   * Distinct from `base`, and the difference matters. `base` is what something
   * is MADE FROM (lime juice from limes), so owning the base probably covers
   * the form. `parent` is what something is a KIND OF, and owning the parent
   * covers nothing — a block of cheddar is not feta. It runs the other way:
   * a recipe asking for "cheese" is probably satisfied by the feta you have,
   * and a food with no suggestions of its own can borrow its parent's.
   */
  parent?: string;
}

/** Specific food -> the category word it is a kind of. See `parent` above. */
const PARENT_OF: Record<string, string> = {
  'chicken-breast': 'chicken', 'chicken-thigh': 'chicken', 'whole-chicken': 'chicken',
  'ground-beef': 'beef', 'ground-pork': 'pork',
  cheddar: 'cheese', mozzarella: 'cheese', feta: 'cheese', ricotta: 'cheese',
  'cream-cheese': 'cheese', 'cottage-cheese': 'cheese', parmesan: 'cheese',
  gruyere: 'cheese', 'swiss-cheese': 'cheese', pecorino: 'cheese',
  salmon: 'fish', cod: 'fish', tuna: 'fish',
  'red-wine': 'wine', 'white-wine': 'wine', 'rice-wine': 'wine',
  sherry: 'wine', vermouth: 'wine',
  'chicken-broth': 'stock', 'beef-broth': 'stock', 'vegetable-broth': 'stock',
  'white-vinegar': 'vinegar', 'balsamic-vinegar': 'vinegar',
  'apple-cider-vinegar': 'vinegar', 'rice-vinegar': 'vinegar',
  'red-wine-vinegar': 'vinegar', 'white-wine-vinegar': 'vinegar',
  'dijon-mustard': 'mustard', 'greek-yogurt': 'yogurt',
  'heavy-cream': 'cream', 'sour-cream': 'cream', 'creme-fraiche': 'cream',
  'brown-sugar': 'sugar', 'powdered-sugar': 'sugar',
  'almond-flour': 'flour', 'dark-chocolate': 'chocolate',
  'chocolate-chips': 'chocolate', 'cherry-tomato': 'tomato',
  'canned-tomatoes': 'tomato', 'sweet-potato': 'potato',
  'red-onion': 'onion', 'red-cabbage': 'cabbage', 'napa-cabbage': 'cabbage',
  'egg-yolk': 'egg', 'egg-white': 'egg',
  'goat-cheese': 'cheese', halloumi: 'cheese', paneer: 'cheese',
  haddock: 'fish', halibut: 'fish', tilapia: 'fish', trout: 'fish', sardine: 'fish',
  'ground-veal': 'veal', 'veal-stock': 'stock', 'raspberry-vinegar': 'vinegar',
  'butternut-squash': 'squash', 'yellow-squash': 'squash',
  'pizza-dough': 'dough', 'puff-pastry': 'dough', 'pie-crust': 'dough', phyllo: 'dough',
};

/** Ids that name a category rather than a food. See `generic` above. */
const GENERIC_IDS = new Set([
  'cheese', 'fish', 'wine', 'stock', 'vinegar', 'mustard', 'chocolate',
  'flour', 'pasta', 'bread', 'sugar', 'oats', 'beef', 'pork', 'chicken',
  'dough',
]);

/**
 * Seed rows: [id, en forms, fr forms, category]
 *
 * No staple flag: whether a food is assumed on hand is decided by
 * `src/constants/staples.ts` alone (`stapleKeyForLine`). A second list here
 * disagreed with it and sent the cook to buy bay leaves the pantry then refused
 * to shelve (#796).
 *
 * Transcribed from the tables this file replaces, so nothing hard-won is lost:
 *   - `frenchToEnglish` and the descriptor lists in groceryListService.ts
 *   - `INGREDIENT_SYNONYMS` in ingredientSynonyms.ts
 *   - `ingredientAliases` and the keys of ingredientSubstitutions.ts
 */
type SeedRow = [string, string[], string[], IngredientCategory];

const BASE_SEED: SeedRow[] = [
  // -- dairy ---------------------------------------------------------------
  ['butter', ['butter', 'unsalted butter', 'salted butter'], ['beurre'], 'dairy'],
  ['milk', ['milk', 'whole milk', '2% milk', 'skim milk'], ['lait'], 'dairy'],
  ['cream', ['cream', 'half and half', 'half & half'], ['crème'], 'dairy'],
  ['heavy-cream', ['heavy cream', 'whipping cream', 'double cream'], ['crème à fouetter', 'crème 35%'], 'dairy'],
  ['sour-cream', ['sour cream'], ['crème sure', 'crème aigre'], 'dairy'],
  ['creme-fraiche', ['creme fraiche', 'crème fraîche'], ['crème fraîche'], 'dairy'],
  ['buttermilk', ['buttermilk'], ['babeurre'], 'dairy'],
  ['yogurt', ['yogurt', 'yoghurt', 'plain yogurt'], ['yogourt', 'yaourt'], 'dairy'],
  ['greek-yogurt', ['greek yogurt'], ['yogourt grec'], 'dairy'],
  ['cheese', ['cheese'], ['fromage'], 'dairy'],
  ['cheddar', ['cheddar'], ['cheddar'], 'dairy'],
  ['mozzarella', ['mozzarella'], ['mozzarella'], 'dairy'],
  ['feta', ['feta'], ['feta'], 'dairy'],
  ['ricotta', ['ricotta'], ['ricotta'], 'dairy'],
  ['cream-cheese', ['cream cheese'], ['fromage à la crème'], 'dairy'],
  ['cottage-cheese', ['cottage cheese'], ['fromage cottage'], 'dairy'],
  ['parmesan', ['parmesan', 'parmigiano', 'parmigiano-reggiano'], ['parmesan'], 'dairy'],
  ['gruyere', ['gruyere', 'gruyère'], ['gruyère'], 'dairy'],
  ['swiss-cheese', ['swiss cheese'], ['fromage suisse'], 'dairy'],
  ['pecorino', ['pecorino', 'pecorino romano'], ['pecorino'], 'dairy'],
  ['evaporated-milk', ['evaporated milk'], ['lait évaporé'], 'dairy'],
  ['condensed-milk', ['condensed milk', 'sweetened condensed milk'], ['lait condensé'], 'dairy'],

  // -- non-dairy milks: NOT forms of milk, they are their own foods --------
  ['coconut-milk', ['coconut milk'], ['lait de coco', 'lait de noix de coco'], 'liquid'],
  ['coconut-cream', ['coconut cream'], ['crème de coco'], 'liquid'],
  ['almond-milk', ['almond milk'], ['lait d’amande'], 'liquid'],
  ['oat-milk', ['oat milk'], ['lait d’avoine'], 'liquid'],
  ['soy-milk', ['soy milk'], ['lait de soja'], 'liquid'],

  // -- protein -------------------------------------------------------------
  ['egg', ['egg', 'eggs', 'whole egg'], ['oeuf', 'œuf', 'oeufs', 'œufs'], 'protein'],
  ['egg-yolk', ['egg yolk', 'egg yolks', 'yolk'], ['jaune d’oeuf', 'jaune d’œuf'], 'protein'],
  ['egg-white', ['egg white', 'egg whites'], ['blanc d’oeuf', 'blanc d’œuf'], 'protein'],
  ['chicken', ['chicken'], ['poulet'], 'protein'],
  ['chicken-breast', ['chicken breast', 'chicken breasts'], ['poitrine de poulet', 'blanc de poulet'], 'protein'],
  ['chicken-thigh', ['chicken thigh', 'chicken thighs'], ['haut de cuisse de poulet', 'cuisse de poulet'], 'protein'],
  ['whole-chicken', ['whole chicken'], ['poulet entier'], 'protein'],
  ['turkey', ['turkey'], ['dinde'], 'protein'],
  ['beef', ['beef', 'steak', 'beef steak'], ['boeuf', 'bœuf', 'steak'], 'protein'],
  ['ground-beef', ['ground beef', 'minced beef'], ['boeuf haché', 'bœuf haché'], 'protein'],
  ['pork', ['pork'], ['porc'], 'protein'],
  ['ground-pork', ['ground pork'], ['porc haché'], 'protein'],
  ['bacon', ['bacon'], ['bacon', 'lard'], 'protein'],
  ['sausage', ['sausage', 'sausages'], ['saucisse', 'saucisses'], 'protein'],
  ['lamb', ['lamb'], ['agneau'], 'protein'],
  ['fish', ['fish', 'white fish'], ['poisson'], 'protein'],
  ['salmon', ['salmon'], ['saumon'], 'protein'],
  ['cod', ['cod'], ['morue'], 'protein'],
  ['tuna', ['tuna'], ['thon'], 'protein'],
  ['shrimp', ['shrimp', 'prawn', 'prawns'], ['crevette', 'crevettes'], 'protein'],
  ['tofu', ['tofu'], ['tofu'], 'protein'],
  ['tempeh', ['tempeh'], ['tempeh'], 'protein'],
  ['chickpeas', ['chickpeas', 'garbanzo beans'], ['pois chiches'], 'protein'],
  ['black-beans', ['black beans'], ['haricots noirs'], 'protein'],
  ['white-beans', ['white beans', 'cannellini beans', 'navy beans'], ['haricots blancs'], 'protein'],
  ['kidney-beans', ['kidney beans', 'red beans'], ['haricots rouges'], 'protein'],
  ['lentils', ['lentils'], ['lentilles'], 'protein'],

  // -- grain ---------------------------------------------------------------
  ['rice', ['rice', 'white rice', 'jasmine rice', 'basmati rice', 'brown rice'], ['riz'], 'grain'],
  ['pasta', ['pasta', 'spaghetti', 'penne', 'noodles'], ['pâtes', 'nouilles'], 'grain'],
  // « pâte » (singular) is dough, « pâtes » is pasta. Listing "pate" under pasta
  // made « pâte à pizza » and « pâte feuilletée » pasta (#772); the dough rows
  // claim the singular so the plural pass cannot hand it back.
  ['dough', ['dough'], ['pâte'], 'grain'],
  ['pizza-dough', ['pizza dough'], ['pâte à pizza'], 'grain'],
  ['puff-pastry', ['puff pastry'], ['pâte feuilletée'], 'grain'],
  ['pie-crust', ['pie crust', 'pie dough', 'pie shell', 'shortcrust pastry'], ['pâte brisée', 'pâte à tarte', 'croûte à tarte'], 'grain'],
  ['phyllo', ['phyllo', 'filo', 'phyllo dough', 'filo pastry'], ['pâte phyllo', 'pâte filo'], 'grain'],
  ['flour', ['flour', 'all-purpose flour', 'ap flour', 'plain flour'], ['farine', 'farine tout usage'], 'grain'],
  ['almond-flour', ['almond flour'], ['farine d’amande'], 'grain'],
  ['bread', ['bread'], ['pain'], 'grain'],
  ['breadcrumbs', ['breadcrumbs', 'bread crumbs', 'panko'], ['chapelure'], 'grain'],
  ['tortilla', ['tortilla', 'tortillas'], ['tortilla', 'tortillas'], 'grain'],
  ['quinoa', ['quinoa'], ['quinoa'], 'grain'],
  ['couscous', ['couscous'], ['couscous'], 'grain'],
  ['bulgur', ['bulgur'], ['boulgour'], 'grain'],
  ['barley', ['barley'], ['orge'], 'grain'],
  ['oats', ['oats', 'rolled oats'], ['avoine', 'flocons d’avoine'], 'grain'],
  ['cornstarch', ['cornstarch', 'corn starch', 'cornflour'], ['fécule de maïs'], 'thickener'],

  // -- fat -----------------------------------------------------------------
  ['olive-oil', ['olive oil', 'extra virgin olive oil', 'evoo'], ['huile d’olive'], 'fat'],
  ['vegetable-oil', ['vegetable oil', 'canola oil', 'corn oil', 'sunflower oil'], ['huile végétale', 'huile de canola'], 'fat'],
  ['coconut-oil', ['coconut oil'], ['huile de coco'], 'fat'],
  ['sesame-oil', ['sesame oil', 'toasted sesame oil'], ['huile de sésame'], 'fat'],
  ['peanut-oil', ['peanut oil'], ['huile d’arachide'], 'fat'],

  // -- vegetable -----------------------------------------------------------
  ['tomato', ['tomato', 'tomatoes'], ['tomate', 'tomates'], 'vegetable'],
  ['cherry-tomato', ['cherry tomato', 'cherry tomatoes', 'grape tomatoes'], ['tomate cerise', 'tomates cerises'], 'vegetable'],
  ['canned-tomatoes', ['canned tomatoes', 'crushed tomatoes', 'diced tomatoes', 'whole peeled tomatoes'], ['tomates en conserve', 'tomates en dés', 'tomates broyées'], 'vegetable'],
  ['potato', ['potato', 'potatoes'], ['pomme de terre', 'pommes de terre', 'patate'], 'vegetable'],
  ['sweet-potato', ['sweet potato', 'sweet potatoes'], ['patate douce', 'patates douces'], 'vegetable'],
  ['carrot', ['carrot', 'carrots'], ['carotte', 'carottes'], 'vegetable'],
  ['celery', ['celery'], ['céleri'], 'vegetable'],
  // A coloured pepper is a bell pepper: "red pepper" used to resolve to `pepper`,
  // the black one, so a pantry holding pepper covered "1 red pepper" (#772).
  ['bell-pepper', ['bell pepper', 'capsicum', 'sweet pepper', 'red pepper', 'green pepper', 'yellow pepper', 'orange pepper'], ['poivron', 'poivrons'], 'vegetable'],
  ['chili-pepper', ['chili pepper', 'chile', 'serrano', 'jalapeno', 'jalapeño'], ['piment', 'piment fort'], 'vegetable'],
  ['spinach', ['spinach'], ['épinards', 'épinard'], 'vegetable'],
  ['kale', ['kale'], ['chou frisé', 'chou kale'], 'vegetable'],
  ['lettuce', ['lettuce', 'romaine', 'iceberg'], ['laitue', 'laitue romaine'], 'vegetable'],
  ['arugula', ['arugula', 'rocket', 'roquette', 'rucola'], ['roquette'], 'vegetable'],
  ['cabbage', ['cabbage'], ['chou', 'choux'], 'vegetable'],
  ['red-cabbage', ['red cabbage'], ['chou rouge'], 'vegetable'],
  ['napa-cabbage', ['napa cabbage'], ['chou nappa', 'chou chinois'], 'vegetable'],
  ['brussels-sprout', ['brussels sprout', 'brussels sprouts'], ['chou de bruxelles', 'choux de bruxelles'], 'vegetable'],
  ['broccoli', ['broccoli'], ['brocoli'], 'vegetable'],
  ['cauliflower', ['cauliflower'], ['chou-fleur'], 'vegetable'],
  ['zucchini', ['zucchini', 'courgette', 'courgettes'], ['courgette', 'courgettes'], 'vegetable'],
  ['eggplant', ['eggplant', 'aubergine'], ['aubergine', 'aubergines'], 'vegetable'],
  ['cucumber', ['cucumber', 'cucumbers'], ['concombre', 'concombres'], 'vegetable'],
  ['mushroom', ['mushroom', 'mushrooms'], ['champignon', 'champignons'], 'vegetable'],
  ['corn', ['corn', 'maize', 'sweetcorn', 'sweet corn'], ['maïs'], 'vegetable'],
  ['peas', ['peas', 'green peas'], ['pois', 'petits pois'], 'vegetable'],
  ['snow-pea', ['snow pea', 'sugar snap pea', 'mangetout'], ['pois mange-tout'], 'vegetable'],
  ['green-beans', ['green beans'], ['haricots verts'], 'vegetable'],
  ['asparagus', ['asparagus'], ['asperge', 'asperges'], 'vegetable'],
  ['leek', ['leek', 'leeks'], ['poireau', 'poireaux'], 'vegetable'],
  ['avocado', ['avocado', 'avocados'], ['avocat', 'avocats'], 'fruit'],
  ['olives', ['olives', 'olive'], ['olive', 'olives'], 'vegetable'],
  ['capers', ['capers'], ['câpres'], 'condiment'],
  ['rutabaga', ['rutabaga', 'swede', 'yellow turnip'], ['rutabaga', 'navet jaune'], 'vegetable'],

  // -- aromatic ------------------------------------------------------------
  ['garlic', ['garlic', 'garlic clove', 'garlic cloves'], ['ail', 'gousse d’ail'], 'aromatic'],
  ['onion', ['onion', 'onions', 'yellow onion', 'white onion', 'sweet onion', 'vidalia onion'], ['oignon', 'oignons'], 'aromatic'],
  ['red-onion', ['red onion'], ['oignon rouge'], 'aromatic'],
  ['shallot', ['shallot', 'shallots'], ['échalote', 'échalotes'], 'aromatic'],
  ['scallion', ['scallion', 'scallions', 'green onion', 'green onions', 'spring onion'], ['oignon vert', 'oignons verts', 'échalote verte'], 'aromatic'],
  ['chives', ['chives', 'chinese chives'], ['ciboulette'], 'herb'],
  ['ginger', ['ginger'], ['gingembre'], 'aromatic'],
  ['lemongrass', ['lemongrass'], ['citronnelle'], 'aromatic'],

  // -- fruit ---------------------------------------------------------------
  ['lemon', ['lemon', 'lemons'], ['citron', 'citrons'], 'fruit'],
  ['lime', ['lime', 'limes'], ['lime', 'limes', 'citron vert', 'citrons verts'], 'fruit'],
  ['lime-leaves', ['lime leaves', 'kaffir lime leaves', 'makrut lime leaves'], ['feuilles de lime', 'feuilles de combava'], 'herb'],
  ['orange', ['orange', 'oranges'], ['orange', 'oranges'], 'fruit'],
  ['apple', ['apple', 'apples'], ['pomme', 'pommes'], 'fruit'],
  ['banana', ['banana', 'bananas'], ['banane', 'bananes'], 'fruit'],
  ['dates', ['dates', 'medjool dates'], ['dattes'], 'fruit'],
  ['grape', ['grape', 'grapes'], ['raisin', 'raisins'], 'fruit'],

  // -- herb ----------------------------------------------------------------
  ['basil', ['basil', 'sweet basil', 'thai basil'], ['basilic'], 'herb'],
  ['parsley', ['parsley', 'italian parsley', 'flat-leaf parsley'], ['persil'], 'herb'],
  ['cilantro', ['cilantro', 'coriander leaves', 'fresh coriander', 'chinese parsley'], ['coriandre'], 'herb'],
  ['mint', ['mint'], ['menthe'], 'herb'],
  ['dill', ['dill'], ['aneth'], 'herb'],
  ['thyme', ['thyme'], ['thym'], 'herb'],
  ['rosemary', ['rosemary'], ['romarin'], 'herb'],
  ['sage', ['sage'], ['sauge'], 'herb'],
  ['oregano', ['oregano'], ['origan'], 'herb'],
  ['tarragon', ['tarragon'], ['estragon'], 'herb'],
  ['bay-leaf', ['bay leaf', 'bay leaves'], ['feuille de laurier', 'laurier'], 'herb'],

  // -- spice ---------------------------------------------------------------
  ['salt', ['salt', 'kosher salt', 'sea salt', 'table salt'], ['sel', 'sel de mer'], 'spice'],
  ['pepper', ['pepper', 'black pepper', 'ground pepper', 'white pepper'], ['poivre', 'poivre noir'], 'spice'],
  ['cumin', ['cumin'], ['cumin'], 'spice'],
  ['paprika', ['paprika'], ['paprika'], 'spice'],
  ['smoked-paprika', ['smoked paprika'], ['paprika fumé'], 'spice'],
  ['coriander-seed', ['coriander', 'coriander seed', 'coriander seeds'], ['graines de coriandre'], 'spice'],
  ['cinnamon', ['cinnamon'], ['cannelle'], 'spice'],
  ['nutmeg', ['nutmeg'], ['muscade'], 'spice'],
  ['cardamom', ['cardamom'], ['cardamome'], 'spice'],
  ['cloves', ['cloves'], ['clous de girofle'], 'spice'],
  ['allspice', ['allspice'], ['piment de la jamaïque'], 'spice'],
  ['turmeric', ['turmeric'], ['curcuma'], 'spice'],
  ['cayenne', ['cayenne', 'cayenne pepper', 'ground red pepper'], ['cayenne', 'poivre de cayenne'], 'spice'],
  ['red-pepper-flakes', ['red pepper flakes', 'crushed red pepper', 'chili flakes'], ['flocons de piment'], 'spice'],
  ['garam-masala', ['garam masala'], ['garam masala'], 'spice'],
  ['italian-seasoning', ['italian seasoning'], ['herbes italiennes'], 'spice'],
  ['steak-spice', ['steak spice', 'montreal steak spice'], ['épices à steak', 'épices à steak de montréal'], 'spice'],
  ['herbes-de-provence', ['herbes de provence'], ['herbes de provence'], 'spice'],
  ['saffron', ['saffron'], ['safran'], 'spice'],

  // -- acid ----------------------------------------------------------------
  ['vinegar', ['vinegar'], ['vinaigre'], 'acid'],
  ['white-vinegar', ['white vinegar'], ['vinaigre blanc'], 'acid'],
  ['balsamic-vinegar', ['balsamic vinegar'], ['vinaigre balsamique'], 'acid'],
  ['apple-cider-vinegar', ['apple cider vinegar', 'cider vinegar'], ['vinaigre de cidre'], 'acid'],
  ['rice-vinegar', ['rice vinegar', 'rice wine vinegar'], ['vinaigre de riz'], 'acid'],
  ['red-wine-vinegar', ['red wine vinegar'], ['vinaigre de vin rouge'], 'acid'],
  ['white-wine-vinegar', ['white wine vinegar'], ['vinaigre de vin blanc'], 'acid'],

  // -- condiment / sauce ---------------------------------------------------
  ['soy-sauce', ['soy sauce', 'tamari', 'shoyu'], ['sauce soya', 'sauce soja'], 'condiment'],
  ['fish-sauce', ['fish sauce'], ['sauce de poisson', 'nuoc-mâm'], 'condiment'],
  ['oyster-sauce', ['oyster sauce'], ['sauce aux huîtres'], 'condiment'],
  ['hoisin', ['hoisin', 'hoisin sauce'], ['sauce hoisin'], 'condiment'],
  ['hot-sauce', ['hot sauce'], ['sauce piquante'], 'condiment'],
  ['sriracha', ['sriracha'], ['sriracha'], 'condiment'],
  ['gochujang', ['gochujang'], ['gochujang'], 'condiment'],
  ['sambal-oelek', ['sambal oelek', 'sambal'], ['sambal oelek'], 'condiment'],
  ['harissa', ['harissa'], ['harissa'], 'condiment'],
  ['pesto', ['pesto'], ['pesto'], 'condiment'],
  ['salsa', ['salsa'], ['salsa'], 'condiment'],
  ['relish', ['relish'], ['relish'], 'condiment'],
  ['worcestershire', ['worcestershire', 'worcestershire sauce'], ['sauce worcestershire'], 'condiment'],
  ['ketchup', ['ketchup'], ['ketchup'], 'condiment'],
  ['mayonnaise', ['mayonnaise', 'mayo'], ['mayonnaise'], 'condiment'],
  ['mustard', ['mustard'], ['moutarde'], 'condiment'],
  ['dijon-mustard', ['dijon mustard', 'dijon'], ['moutarde de dijon'], 'condiment'],
  ['miso', ['miso'], ['miso'], 'condiment'],
  ['tahini', ['tahini'], ['tahini'], 'condiment'],
  ['peanut-butter', ['peanut butter'], ['beurre d’arachide', 'beurre de cacahuète'], 'condiment'],
  ['marinara', ['marinara', 'marinara sauce'], ['sauce marinara'], 'condiment'],

  // -- liquid --------------------------------------------------------------
  ['water', ['water'], ['eau'], 'liquid'],
  ['stock', ['stock', 'broth'], ['bouillon'], 'liquid'],
  ['chicken-broth', ['chicken broth', 'chicken stock'], ['bouillon de poulet'], 'liquid'],
  ['beef-broth', ['beef broth', 'beef stock'], ['bouillon de boeuf', 'bouillon de bœuf'], 'liquid'],
  ['vegetable-broth', ['vegetable broth', 'vegetable stock'], ['bouillon de légumes'], 'liquid'],
  ['wine', ['wine'], ['vin'], 'liquid'],
  ['red-wine', ['red wine'], ['vin rouge'], 'liquid'],
  ['white-wine', ['white wine'], ['vin blanc'], 'liquid'],
  ['rice-wine', ['rice wine', 'shaoxing wine', 'shaoxing rice wine', 'mirin'], ['vin de riz'], 'liquid'],
  ['sherry', ['sherry', 'dry sherry', 'fino sherry'], ['xérès', 'sherry'], 'liquid'],
  ['vermouth', ['vermouth', 'dry vermouth'], ['vermouth'], 'liquid'],
  ['beer', ['beer'], ['bière'], 'liquid'],

  // -- sweetener -----------------------------------------------------------
  ['sugar', ['sugar', 'white sugar', 'granulated sugar', 'caster sugar'], ['sucre'], 'sweetener'],
  ['brown-sugar', ['brown sugar', 'light brown sugar', 'dark brown sugar'], ['cassonade', 'sucre brun'], 'sweetener'],
  ['powdered-sugar', ['powdered sugar', 'icing sugar', 'confectioners sugar'], ['sucre à glacer'], 'sweetener'],
  ['honey', ['honey', 'raw honey'], ['miel'], 'sweetener'],
  ['maple-syrup', ['maple syrup', 'pure maple syrup'], ['sirop d’érable'], 'sweetener'],
  ['molasses', ['molasses'], ['mélasse'], 'sweetener'],
  ['agave', ['agave', 'agave nectar'], ['sirop d’agave'], 'sweetener'],

  // -- leavening / baking --------------------------------------------------
  ['baking-powder', ['baking powder'], ['poudre à pâte', 'levure chimique'], 'leavening'],
  ['baking-soda', ['baking soda', 'bicarbonate of soda'], ['bicarbonate de soude'], 'leavening'],
  ['yeast', ['yeast', 'active dry yeast', 'instant yeast'], ['levure'], 'leavening'],
  ['vanilla-extract', ['vanilla extract', 'vanilla'], ['extrait de vanille', 'vanille'], 'other'],
  ['almond-extract', ['almond extract'], ['extrait d’amande'], 'other'],
  ['nutritional-yeast', ['nutritional yeast'], ['levure alimentaire'], 'other'],
  ['gelatin', ['gelatin'], ['gélatine'], 'other'],
  ['cream-of-tartar', ['cream of tartar'], ['crème de tartre'], 'other'],

  // -- nuts and seeds ------------------------------------------------------
  ['peanuts', ['peanuts', 'peanut'], ['arachides', 'cacahuètes'], 'nut'],
  ['almonds', ['almonds', 'almond'], ['amandes', 'amande'], 'nut'],
  ['walnuts', ['walnuts', 'walnut'], ['noix'], 'nut'],
  ['pecans', ['pecans', 'pecan'], ['pacanes'], 'nut'],
  ['cashews', ['cashews', 'cashew'], ['noix de cajou'], 'nut'],
  ['pine-nuts', ['pine nuts'], ['pignons', 'pignons de pin'], 'nut'],
  ['pistachios', ['pistachios', 'pistachio'], ['pistaches'], 'nut'],
  ['sesame-seeds', ['sesame seeds'], ['graines de sésame'], 'seed'],
  ['chia-seeds', ['chia seeds'], ['graines de chia'], 'seed'],
  ['flax-seeds', ['flax seeds', 'flaxseed', 'linseed'], ['graines de lin'], 'seed'],
  ['sunflower-seeds', ['sunflower seeds'], ['graines de tournesol'], 'seed'],
  ['coconut', ['coconut', 'shredded coconut'], ['noix de coco'], 'other'],

  // -- chocolate -----------------------------------------------------------
  ['chocolate', ['chocolate'], ['chocolat'], 'chocolate'],
  ['dark-chocolate', ['dark chocolate'], ['chocolat noir'], 'chocolate'],
  ['chocolate-chips', ['chocolate chips'], ['pépites de chocolat'], 'chocolate'],
  ['cocoa-powder', ['cocoa powder', 'cocoa', 'unsweetened cocoa'], ['poudre de cacao', 'cacao'], 'chocolate'],

  // -- foods that appear only as substitutes -------------------------------
  //
  // Every name the substitution table suggests needs an identity, or the sheet
  // cannot tell you that you already own one. Before these entries only 83 of
  // 279 simple substitute names resolved to anything at all, so "do I have a
  // stand-in?" was unanswerable for most of the table.
  ['applesauce', ['applesauce', 'apple sauce'], ['compote de pommes'], 'fruit'],
  ['aquafaba', ['aquafaba', 'chickpea water'], ['aquafaba'], 'protein'],
  ['seitan', ['seitan'], ['seitan'], 'protein'],
  ['jackfruit', ['jackfruit'], ['jacquier'], 'fruit'],
  ['tvp', ['tvp', 'textured vegetable protein'], ['protéine végétale texturée'], 'protein'],
  ['plant-mince', ['beyond/impossible meat', 'beyond meat', 'impossible meat', 'plant-based mince'], ['viande végétale'], 'protein'],
  ['edamame', ['edamame'], ['edamame'], 'protein'],
  ['lima-beans', ['lima beans', 'butter beans'], ['haricots de lima'], 'protein'],
  ['soy-nuts', ['soy nuts'], ['noix de soja'], 'nut'],
  ['hummus', ['hummus'], ['houmous'], 'condiment'],
  ['arctic-char', ['arctic char'], ['omble chevalier'], 'protein'],
  ['trout', ['trout'], ['truite'], 'protein'],

  ['arrowroot', ['arrowroot', 'arrowroot powder'], ['arrow-root', 'marante'], 'thickener'],
  ['tapioca-starch', ['tapioca starch', 'tapioca flour'], ['fécule de tapioca'], 'thickener'],
  ['konjac', ['konjac', 'shirataki'], ['konjac'], 'thickener'],
  ['potassium-bicarbonate', ['potassium bicarbonate'], ['bicarbonate de potassium'], 'leavening'],

  ['asiago', ['asiago'], ['asiago'], 'dairy'],
  ['carob', ['carob'], ['caroube'], 'chocolate'],
  ['carob-powder', ['carob powder'], ['poudre de caroube'], 'chocolate'],
  ['semisweet-chips', ['semisweet chips', 'semi-sweet chocolate chips'], ['pépites mi-sucrées'], 'chocolate'],

  ['bok-choy', ['bok choy', 'pak choi'], ['bok choy', 'pak choï'], 'vegetable'],
  ['bok-choy-stems', ['bok choy stems'], ['tiges de bok choy'], 'vegetable'],
  ['broccolini', ['broccolini', 'broccoli rabe', 'rapini'], ['brocolini', 'rapini'], 'vegetable'],
  ['romanesco', ['romanesco'], ['romanesco'], 'vegetable'],
  ['collard-greens', ['collard greens'], ['chou cavalier'], 'vegetable'],
  ['swiss-chard', ['swiss chard', 'chard'], ['bette à carde', 'blette'], 'vegetable'],
  ['butternut-squash', ['butternut squash', 'butternut'], ['courge musquée'], 'vegetable'],
  ['pumpkin', ['pumpkin'], ['citrouille', 'potiron'], 'vegetable'],
  ['yellow-squash', ['yellow squash', 'summer squash'], ['courge jaune'], 'vegetable'],
  ['parsnip', ['parsnip', 'parsnips'], ['panais'], 'vegetable'],
  ['turnip', ['turnip', 'turnips'], ['navet', 'navets'], 'vegetable'],
  ['jicama', ['jicama'], ['jicama'], 'vegetable'],
  ['hearts-of-palm', ['hearts of palm'], ['coeurs de palmier'], 'vegetable'],
  ['hominy', ['hominy'], ['maïs nixtamalisé'], 'grain'],
  ['tomatillo', ['tomatillo', 'tomatillos'], ['tomatille'], 'vegetable'],
  ['poblano', ['poblano', 'poblano pepper'], ['piment poblano'], 'vegetable'],
  ['cubanelle', ['cubanelle', 'cubanelle pepper'], ['piment cubanelle'], 'vegetable'],
  ['fennel', ['fennel'], ['fenouil'], 'vegetable'],
  ['fennel-fronds', ['fennel fronds'], ['frondes de fenouil'], 'herb'],
  ['fennel-stalks', ['fennel stalks'], ['tiges de fenouil'], 'vegetable'],
  ['pickles', ['pickles', 'pickle', 'chopped pickle', 'gherkins'], ['cornichons'], 'condiment'],
  ['horseradish', ['horseradish'], ['raifort'], 'condiment'],

  ['chervil', ['chervil'], ['cerfeuil'], 'herb'],
  ['marjoram', ['marjoram'], ['marjolaine'], 'herb'],
  ['culantro', ['culantro'], ['culantro'], 'herb'],
  ['galangal', ['galangal'], ['galanga'], 'aromatic'],
  ['asafoetida', ['asafoetida', 'hing'], ['asafoetida'], 'spice'],
  ['mace', ['mace'], ['macis'], 'spice'],
  ['fennel-seed', ['fennel seed', 'fennel seeds'], ['graines de fenouil'], 'spice'],
  ['chipotle-powder', ['chipotle powder'], ['poudre de chipotle'], 'spice'],
  ['poultry-seasoning', ['poultry seasoning'], ['assaisonnement à volaille'], 'spice'],
  ['taco-seasoning', ['taco seasoning'], ['assaisonnement à tacos'], 'spice'],
  ['peppercorns', ['pickled green peppercorns', 'green peppercorns'], ['poivre vert'], 'spice'],
  ['poppy-seeds', ['poppy seeds'], ['graines de pavot'], 'seed'],
  ['hemp-seeds', ['hemp seeds'], ['graines de chanvre'], 'seed'],
  ['pumpkin-seeds', ['pumpkin seeds', 'pepitas'], ['graines de citrouille'], 'seed'],
  ['hazelnuts', ['hazelnuts', 'hazelnut'], ['noisettes'], 'nut'],
  ['macadamia-nuts', ['macadamia nuts', 'macadamia'], ['noix de macadamia'], 'nut'],

  ['chili-oil', ['chili oil'], ['huile pimentée'], 'condiment'],
  ['chili-paste', ['chili paste'], ['pâte de piment'], 'condiment'],
  ['liquid-aminos', ['liquid aminos', 'coconut aminos'], ['acides aminés liquides'], 'condiment'],
  ['crushed-crackers', ['crushed crackers', 'cracker crumbs'], ['craquelins émiettés'], 'grain'],

  ['cranberry-juice', ['cranberry juice'], ['jus de canneberge'], 'liquid'],
  ['pomegranate-juice', ['pomegranate juice'], ['jus de grenade'], 'liquid'],
  ['simple-syrup', ['simple syrup'], ['sirop simple'], 'sweetener'],
  ['stevia', ['stevia'], ['stévia'], 'sweetener'],
  ['monk-fruit', ['monk fruit'], ['fruit du moine'], 'sweetener'],
  ['amaretto', ['amaretto'], ['amaretto'], 'liquid'],
  ['dried-figs', ['dried figs', 'figs'], ['figues séchées', 'figues'], 'fruit'],
  ['prunes', ['prunes'], ['pruneaux'], 'fruit'],
  ['grapeseed-oil', ['grapeseed oil'], ['huile de pépins de raisin'], 'fat'],

  // -- what real pantries hold ---------------------------------------------
  //
  // Since the planners rank by this vocabulary too (`pantryMatch.ts`), a food it
  // has never heard of is a shelf item no ranking can see across languages. On
  // 2026-09-28 a fifth of active pantry rows resolved to nothing, and most of
  // them were ordinary produce and meat: fraises, bleuets, radis, jambon, veau.
  // Left out on purpose: plum (French "prunes" is plums, English "prunes" is
  // dried plums) and mussels ("moule" is also the baking pan, and scraped
  // French recipes carry that line in their ingredients).
  ['strawberry', ['strawberry', 'strawberries'], ['fraise', 'fraises'], 'fruit'],
  ['blueberry', ['blueberry', 'blueberries'], ['bleuet', 'bleuets', 'myrtille', 'myrtilles'], 'fruit'],
  ['raspberry', ['raspberry', 'raspberries'], ['framboise', 'framboises'], 'fruit'],
  ['cherry', ['cherry', 'cherries'], ['cerise', 'cerises'], 'fruit'],
  ['cranberry', ['cranberry', 'cranberries'], ['canneberge', 'canneberges'], 'fruit'],
  ['pineapple', ['pineapple'], ['ananas'], 'fruit'],
  ['pear', ['pear', 'pears'], ['poire', 'poires'], 'fruit'],
  ['peach', ['peach', 'peaches'], ['pêche', 'pêches'], 'fruit'],
  ['nectarine', ['nectarine', 'nectarines'], ['nectarine', 'nectarines'], 'fruit'],
  ['apricot', ['apricot', 'apricots'], ['abricot', 'abricots'], 'fruit'],
  ['mango', ['mango', 'mangoes', 'mangos'], ['mangue', 'mangues'], 'fruit'],
  ['kiwi', ['kiwi', 'kiwis', 'kiwifruit'], ['kiwi', 'kiwis'], 'fruit'],
  ['grapefruit', ['grapefruit', 'grapefruits'], ['pamplemousse', 'pamplemousses'], 'fruit'],
  ['pomegranate', ['pomegranate', 'pomegranate seeds', 'pomegranate arils'], ['grenade', 'grains de grenade'], 'fruit'],
  ['pomegranate-molasses', ['pomegranate molasses'], ['mélasse de grenade'], 'condiment'],
  ['melon', ['melon', 'honeydew', 'honeydew melon'], ['melon', 'melon miel'], 'fruit'],
  ['cantaloupe', ['cantaloupe'], ['cantaloup'], 'fruit'],
  ['watermelon', ['watermelon'], ['melon d’eau', 'pastèque'], 'fruit'],
  ['rhubarb', ['rhubarb'], ['rhubarbe'], 'vegetable'],
  ['radish', ['radish', 'radishes', 'watermelon radish'], ['radis', 'radis melon'], 'vegetable'],
  ['beet', ['beet', 'beets', 'beetroot'], ['betterave', 'betteraves'], 'vegetable'],
  ['squash', ['squash', 'winter squash', 'acorn squash'], ['courge', 'courges', 'courge poivrée'], 'vegetable'],
  ['endive', ['endive', 'endives', 'belgian endive'], ['endive', 'endives'], 'vegetable'],
  ['sunchoke', ['jerusalem artichoke', 'jerusalem artichokes', 'sunchoke', 'sunchokes'], ['topinambour', 'topinambours'], 'vegetable'],
  ['sorrel', ['sorrel'], ['oseille'], 'herb'],
  ['oyster-mushroom', ['oyster mushroom', 'oyster mushrooms'], ['pleurote', 'pleurotes'], 'vegetable'],
  ['kimchi', ['kimchi'], ['kimchi'], 'condiment'],
  ['gnocchi', ['gnocchi'], ['gnocchi', 'gnocchis'], 'grain'],
  ['ham', ['ham'], ['jambon'], 'protein'],
  ['prosciutto', ['prosciutto'], ['prosciutto', 'jambon cru'], 'protein'],
  ['pancetta', ['pancetta'], ['pancetta'], 'protein'],
  ['chorizo', ['chorizo'], ['chorizo'], 'protein'],
  ['veal', ['veal'], ['veau'], 'protein'],
  ['ground-veal', ['ground veal'], ['veau haché'], 'protein'],
  // Named so the longer surface wins: "fond de veau" is not veal, "gras de
  // canard" is not duck, "vinaigre de framboise" is not raspberries.
  ['veal-stock', ['veal stock', 'veal demi-glace'], ['fond de veau'], 'liquid'],
  ['duck-fat', ['duck fat'], ['gras de canard'], 'fat'],
  ['raspberry-vinegar', ['raspberry vinegar'], ['vinaigre de framboise'], 'acid'],
  ['jam', ['jam', 'preserves', 'apricot jam', 'strawberry jam', 'raspberry jam'], ['confiture', 'confiture d’abricots', 'confiture de fraises', 'confiture de framboises'], 'condiment'],
  ['duck', ['duck', 'duck breast'], ['canard', 'magret de canard'], 'protein'],
  ['anchovy', ['anchovy', 'anchovies', 'anchovy fillets'], ['anchois', 'filets d’anchois'], 'protein'],
  ['sardine', ['sardine', 'sardines'], ['sardine', 'sardines'], 'protein'],
  ['haddock', ['haddock'], ['aiglefin'], 'protein'],
  ['halibut', ['halibut'], ['flétan'], 'protein'],
  ['tilapia', ['tilapia'], ['tilapia'], 'protein'],
  ['scallops', ['scallop', 'scallops'], ['pétoncle', 'pétoncles'], 'protein'],
  ['crab', ['crab', 'crabmeat', 'crab meat'], ['crabe', 'chair de crabe'], 'protein'],
  ['lobster', ['lobster'], ['homard'], 'protein'],
  ['goat-cheese', ['goat cheese', 'chevre', 'chèvre'], ['fromage de chèvre', 'chèvre'], 'dairy'],
  ['halloumi', ['halloumi'], ['halloumi'], 'dairy'],
  ['paneer', ['paneer'], ['paneer'], 'dairy'],
];

/**
 * Derived forms: [id, base, form, en forms, fr forms]
 *
 * These are the entries `normalizeIngredientName` cannot represent, because it
 * strips the very word that distinguishes them. A caller decides for itself
 * whether owning the base covers the form — see `ingredientRelations.ts`.
 */
type DerivedRow = [string, string, IngredientForm, string[], string[]];

const DERIVED_SEED: DerivedRow[] = [
  ['lemon-juice', 'lemon', 'juice', ['lemon juice'], ['jus de citron']],
  ['lemon-zest', 'lemon', 'zest', ['lemon zest', 'lemon peel'], ['zeste de citron']],
  ['lime-juice', 'lime', 'juice', ['lime juice'], ['jus de lime', 'jus de citron vert']],
  ['lime-zest', 'lime', 'zest', ['lime zest', 'lime peel'], ['zeste de lime']],
  ['orange-juice', 'orange', 'juice', ['orange juice'], ['jus d’orange']],
  ['orange-zest', 'orange', 'zest', ['orange zest', 'orange peel'], ['zeste d’orange']],
  ['garlic-powder', 'garlic', 'powder', ['garlic powder'], ['ail en poudre', 'poudre d’ail']],
  ['onion-powder', 'onion', 'powder', ['onion powder'], ['oignon en poudre']],
  ['ginger-powder', 'ginger', 'powder', ['ground ginger', 'ginger powder'], ['gingembre moulu']],
  ['tomato-paste', 'tomato', 'paste', ['tomato paste'], ['pâte de tomate', 'concentré de tomate']],
  ['tomato-sauce', 'tomato', 'puree', ['tomato sauce', 'tomato puree', 'passata'], ['sauce tomate', 'purée de tomate']],
  ['chili-powder', 'chili-pepper', 'powder', ['chili powder'], ['poudre de chili']],
  ['curry-powder', 'turmeric', 'powder', ['curry powder', 'curry'], ['poudre de cari', 'cari']],
  ['mustard-powder', 'mustard', 'powder', ['mustard powder', 'dry mustard'], ['moutarde en poudre']],
  ['apple-juice', 'apple', 'juice', ['apple juice'], ['jus de pomme']],
  ['grape-juice', 'grape', 'juice', ['grape juice'], ['jus de raisin']],
  ['dried-mushroom', 'mushroom', 'dried', ['dried mushrooms', 'dried porcini'], ['champignons séchés']],
  ['dried-cranberries', 'cranberry', 'dried', ['dried cranberries', 'craisins'], ['canneberges séchées']],
  ['dried-apricots', 'apricot', 'dried', ['dried apricots'], ['abricots séchés']],
];

/** Every entry, base and derived, keyed by id. */
export const INGREDIENT_ENTRIES: Record<string, IngredientEntry> = {};

for (const [id, en, fr, category] of BASE_SEED) {
  INGREDIENT_ENTRIES[id] = {
    id, en, fr, category,
    ...(GENERIC_IDS.has(id) ? { generic: true } : {}),
    ...(PARENT_OF[id] ? { parent: PARENT_OF[id] } : {}),
  };
}
for (const [id, base, form, en, fr] of DERIVED_SEED) {
  const from = INGREDIENT_ENTRIES[base];
  if (!from) {
    // A derived form pointing at a base that does not exist would resolve to an
    // id no caller can reason about. Fail loudly at module load, not in a sheet.
    throw new Error(`ingredientVocabulary: derived "${id}" has unknown base "${base}"`);
  }
  INGREDIENT_ENTRIES[id] = { id, en, fr, category: from.category, form, base };
}

for (const [id, parent] of Object.entries(PARENT_OF)) {
  if (INGREDIENT_ENTRIES[id] && !INGREDIENT_ENTRIES[parent]) {
    throw new Error(`ingredientVocabulary: "${id}" has unknown parent "${parent}"`);
  }
}

/**
 * Walk `base` then `parent` until something answers, e.g. for a table that has
 * an entry for `chicken` but not `chicken-thigh`.
 */
export function broaderIds(id: CanonicalIngredientId | null): CanonicalIngredientId[] {
  const chain: CanonicalIngredientId[] = [];
  let current = id ? INGREDIENT_ENTRIES[id] : undefined;
  const seen = new Set<string>(id ? [id] : []);

  while (current) {
    const next = current.base ?? current.parent;
    if (!next || seen.has(next)) break;
    seen.add(next);
    chain.push(next);
    current = INGREDIENT_ENTRIES[next];
  }
  return chain;
}

export type CanonicalIngredientId = string;

/**
 * Surface form -> id. Built once at module load.
 *
 * Longest form first, so a lookup that walks candidate phrases finds
 * "rice wine" before "rice" and "peanut butter" before "butter". This ordering
 * is the fix for the bug that started all of this: the old matcher iterated
 * object-literal declaration order, so "rice" (declared at line 215) beat
 * "wine" (line 367) and "Shaoxing rice wine or dry sherry" offered quinoa.
 */
const SURFACE_TO_ID = new Map<string, string>();

/** Strip accents and punctuation so "crème" and "creme" are one key. */
export function foldSurface(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/['’]/g, ' ')
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

for (const entry of Object.values(INGREDIENT_ENTRIES)) {
  for (const surface of [...entry.en, ...entry.fr]) {
    const key = foldSurface(surface);
    if (!key) continue;
    // First writer wins: a surface form shared by two entries belongs to the one
    // that declared it, and the duplicate is a vocabulary bug worth seeing.
    if (!SURFACE_TO_ID.has(key)) SURFACE_TO_ID.set(key, entry.id);
  }
}

/**
 * Second pass: naive plural/singular variants, but only for keys nobody claimed.
 *
 * French recipes pluralize the head of a compound ("pâte de tomates" against a
 * vocabulary that lists "pâte de tomate"), and listing both spellings by hand
 * for every entry is exactly the kind of table that drifts. Running this after
 * the explicit pass means a hand-written form always beats a generated one.
 */
for (const [key, id] of [...SURFACE_TO_ID.entries()]) {
  const variants = key.endsWith('s')
    ? [key.slice(0, -1)]
    : [`${key}s`];
  // Pluralize the last word of a compound too: "pate de tomate" -> "pate de tomates".
  const words = key.split(' ');
  if (words.length > 1) {
    const last = words[words.length - 1];
    const swapped = last.endsWith('s') ? last.slice(0, -1) : `${last}s`;
    variants.push([...words.slice(0, -1), swapped].join(' '));
  }
  for (const variant of variants) {
    if (variant.length > 2 && !SURFACE_TO_ID.has(variant)) SURFACE_TO_ID.set(variant, id);
  }
}

/** All surface forms, longest first — the order candidate matching must use. */
export const SURFACE_FORMS: string[] = [...SURFACE_TO_ID.keys()].sort(
  (a, b) => b.length - a.length || a.localeCompare(b)
);

export function idForSurface(surface: string): CanonicalIngredientId | null {
  return SURFACE_TO_ID.get(foldSurface(surface)) ?? null;
}

export function entryFor(id: CanonicalIngredientId | null): IngredientEntry | null {
  return id ? INGREDIENT_ENTRIES[id] ?? null : null;
}

/** Display name in the requested language, falling back to English. */
export function displayName(id: CanonicalIngredientId, language: 'en' | 'fr' = 'en'): string {
  const entry = INGREDIENT_ENTRIES[id];
  if (!entry) return id;
  return (language === 'fr' ? entry.fr[0] : entry.en[0]) || entry.en[0] || id;
}
